import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { animated, useSpring } from 'react-spring';
import { RTI_DOWNSTREAM_SHARE } from '@rsos/constants/collaborator';
import { SOURCE_RCS, SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { TEXT } from '@rsos/constants/locationTypes';
import {
  RCS_CONVERSATION_STATUS_PENDING,
  RCS_CONVERSATION_STATUS_ACTIVE,
  RCS_CONVERSATION_STATUS_ENDED,
  RCS_CONVERSATION_STATUS_TIMED_OUT,
} from '@rsos/constants/rcsStatuses';
import { useBooleanFlagValue } from '@rsos/feature-flags';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import useHasAccessToGoogleRCS from '@rsos/hooks/useHasAccessToGoogleRCS';
import useIsTT911Enabled from '@rsos/hooks/useIsTT911Enabled';
import { useCurrentPSAPAccountID } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { useGetConversationMetaData } from '@rsos/reduxDataStore/src/features/conversations/utils';
import { setCallExpired } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  getEffectiveCollaborator,
  isRtiDownstreamSharer,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { setHoveredID } from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import {
  useGetRcsConversationMetadataQuery,
  useLazyTimeoutRcsConversationQuery,
} from '@rsos/reduxDataStore/src/features/rcs/rcsApi';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import {
  setRcsConversationStatus,
  dismissRcsToast,
} from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { getTokens } from '@rsos/sinatra';
import getCallStatusName from '@rsos/utils/getCallStatusName';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { getExpiredCallAge } from '@rsos/utils/metaTags';
import { displayMilitaryTime } from '@rsos/utils/timeHelpers';
import usePortalType from '@rsos/utils/usePortalType';
import { RCS_TIMEOUT } from '../../../constants/rcsStatus';
import isTextBasedSource from '../../../utils/isSourceSMS';
import { logMissingTT911Capability } from '../../../utils/logMissingTT911Capability';
import {
  LiveVideoSession,
  MediaAvailable,
  ZeroEyesEvent,
} from '../../AdditionalData/AdrQuery/QueueText';
import UnplottableAddressNotice from '../../JurisdictionView/Map/UnplottableAddressNotice';
import {
  getCivicAddress,
  isUnplottableAddress,
  UNGEOCODED_CALLS_FLAG,
} from '../../JurisdictionView/Map/unplottableAddress';
import { useChatComponents } from '../../JurisdictionView/useChatComponents';
import OnsitePersonnelNotifiedText from '../OnsitePersonnelNotifiedText';
import { useQueueItemNotification } from '../QueueNotifications/QueueNotificationsContext';
import {
  Container,
  IconBox,
  CallBox,
  PhoneNumber,
  PhoneDetails,
  CallStatusTag,
  CallStatus,
  AirAmbulanceStatus,
  SiriusXMMessage,
  RegularNewMessage,
  TimeBox,
  Time,
  RemoveCall,
  ConnectedSiteMessage,
  CollaboratorMessage,
} from './EmergencyCall.styles';
import EmergencyCallIcons from './EmergencyCallIcons';
import TextMessageStatus from './TextMessageStatus';
import {
  trackViewRCSToast,
  trackCloseRCSToast,
  trackRcsTimeout,
} from './emergencyCallTrackings';
import { fetchLocationShareLocation } from './fetchLocationSharedLocation';
import { getCollaboratorTypeDisplayName, toastRcsAvailable } from './helpers';
import useFullSelectedNumberData from './hooks/useFullSelectedNumberData';

const expiredCallAge = getExpiredCallAge();

const EmergencyCall = ({
  applicationType = '',
  airAmbulanceStatus = '',
  classOfService = '',
  callerID = '',
  callStatus = '',
  rightClickedCallerID = '',
  hasAgent511Access = false,
  hasGoogleCarCrashData = false,
  hasGoogleCallerInfo = false,
  isApple = false,
  isConnectedCar = false,
  isFord = false,
  primaryLabel = '',
  isCallerIn911informBuilding = false,
  isCallerInConnectedSitesBuilding = false,
  isCallReceived = false,
  isCallShared = false,
  isManualSearch = false,
  isMapMarkerHovered = false,
  isRemoveCallVisible = false,
  isSearchResult = false,
  isSearchResultHovered = false,
  isSelected = false,
  isTT911 = false,
  isAdmin = false,
  isUnread = false,
  isUnseen = false,
  onRemoveCall = () => {},
  setCallerID = () => {},
  source = '',
  tt911ADR = null,
  ui = {},
  onViewRcsEvent = () => {},
}) => {
  const dispatch = useDispatch();
  const ungeocodedCallsEnabled = useBooleanFlagValue(
    UNGEOCODED_CALLS_FLAG,
    false,
  );
  const eventShareData = useSelector(state => state.eventShare[callerID]) || [];
  const token = getTokens()?.token;
  const { primaryChatKey } = useChatComponents(callerID);

  const { isPremiumUser } = usePortalType();
  const hasAirAmbulanceAccess = useHasAccessToAirAmbulance();

  const tt911Message = tt911ADR?.data?.message;
  const [googleIndicator, setGoogleIndicator] = useState(null);
  const [displayRemoveCall, setDisplayRemoveCall] =
    useState(isRemoveCallVisible);
  const airAmbulanceCallsRequests = useSelector(
    state => state.airAmbulance?.requests?.calls || {},
  );
  const callWithAirAmbulanceRequest = airAmbulanceCallsRequests[callerID];

  const isConversationUnread = useSelector(
    state => state.conversations?.[callerID]?.isUnread,
  );

  const rawCollaborator = useSelector(
    state => state.emergencyCalls.callQueue[callerID]?.collaborator,
  );
  const currentAccountId = useCurrentPSAPAccountID();
  // Suppresses the primary agency's own copy of an rti_downstream share
  // event -- see getEffectiveCollaborator. Every field read below comes off
  // this value (not rawCollaborator) so suppression can't be forgotten for
  // one field while applied to another.
  const collaborator = getEffectiveCollaborator(
    rawCollaborator,
    currentAccountId,
  );
  const collaboratorType = collaborator?.type;
  const isCollaborator = !!collaboratorType;
  const isCollaboratorUnread = collaborator?.isUnread;
  // RTI-routed calls get the yellow arrow icon in EmergencyCallIcons instead of the collaborator stripe.
  const isCollaboratorStripeVisible =
    isCollaborator && collaboratorType !== RTI_DOWNSTREAM_SHARE;
  // Computed off rawCollaborator, not the suppressed collaborator above, since
  // the sharer's own copy is exactly what getEffectiveCollaborator nulls out.
  const isRtiSharer = isRtiDownstreamSharer(rawCollaborator, currentAccountId);

  const callStatusDisplayName = useMemo(() => {
    if (airAmbulanceStatus) {
      return airAmbulanceStatus;
    }

    if (hasAirAmbulanceAccess && callWithAirAmbulanceRequest) {
      const statuses = callWithAirAmbulanceRequest.body?.status || [];
      const statusName = statuses[statuses.length - 1];
      return statusName?.update || '';
    }
    const callStatusName = getCallStatusName(callStatus);
    return callStatusName;
  }, [
    airAmbulanceStatus,
    callStatus,
    callWithAirAmbulanceRequest,
    hasAirAmbulanceAccess,
  ]);

  const [rcsSecondsLeft, setRcsSecondsLeft] = useState(RCS_TIMEOUT);
  const [rcsTimerRunning, setRcsTimerRunning] = useState(false);
  const rcsConversation = useSelectedRcsConversation(callerID);

  // get RCS conversation metadata for tracking
  const rcsConversationId = useMemo(
    () => rcsConversation?.data?.conversation_id,
    [rcsConversation],
  );

  const rcsSessionId = useMemo(
    () => rcsConversation?.data?.session_id,
    [rcsConversation],
  );

  const [triggerTimeoutRcsConversation] = useLazyTimeoutRcsConversationQuery();

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      )?.name,
  );

  const animateProps = useSpring({
    config: { duration: 200 },
    display: displayRemoveCall ? 'flex' : 'none',
    opacity: displayRemoveCall ? 1 : 0,
  });

  useEffect(() => {
    rightClickedCallerID === callerID
      ? setDisplayRemoveCall(true)
      : setDisplayRemoveCall(false);
  }, [rightClickedCallerID, callerID]);

  useEffect(() => {
    if (hasGoogleCallerInfo && hasGoogleCarCrashData) {
      setGoogleIndicator('Language, Crash Detection Sensor');
    } else if (hasGoogleCallerInfo) {
      setGoogleIndicator('Language');
    } else if (hasGoogleCarCrashData) {
      setGoogleIndicator('Crash Detection Sensor');
    }
  }, [hasGoogleCallerInfo, hasGoogleCarCrashData]);

  const hasGoogleRCSAccess = useHasAccessToGoogleRCS();

  const fullSelectedNumberData = useFullSelectedNumberData(callerID);

  const mostRecentLocation = useMemo(
    () => getMostRecentLoc(fullSelectedNumberData.callData),
    [fullSelectedNumberData],
  );

  const elsSource = useMemo(() => {
    if (!mostRecentLocation) return;
    return mostRecentLocation.source;
  }, [mostRecentLocation]);

  const isSMS = useMemo(
    () =>
      fullSelectedNumberData?.callData?.isTT911 ||
      !!fullSelectedNumberData?.textFrom911Data?.data?.conversation_id ||
      isTextBasedSource(mostRecentLocation?.source) ||
      mostRecentLocation?.class_of_service_category === TEXT,
    [
      fullSelectedNumberData?.callData?.isTT911,
      fullSelectedNumberData?.textFrom911Data?.data?.conversation_id,
      mostRecentLocation?.source,
      mostRecentLocation?.class_of_service_category,
    ],
  );

  const isExpired = useMemo(() => {
    return !!fullSelectedNumberData?.callData?.isCallExpired;
  }, [fullSelectedNumberData?.callData?.isCallExpired]);

  const rcsStatusText = useMemo(() => {
    if (!rcsConversation || !rcsConversation.data) return '';
    const { conversation_status } = rcsConversation.data;
    if (conversation_status === RCS_CONVERSATION_STATUS_PENDING) {
      if (rcsSecondsLeft > 0) {
        return `RCS AVAILABLE FOR ${rcsSecondsLeft} SEC`;
      }
      return `RCS TIMED OUT`;
    }
    if (conversation_status === RCS_CONVERSATION_STATUS_TIMED_OUT)
      return 'RCS TIMED OUT';
    if (conversation_status === RCS_CONVERSATION_STATUS_ENDED)
      return 'RCS ENDED';
    if (conversation_status === RCS_CONVERSATION_STATUS_ACTIVE)
      return 'RCS MESSAGE';
    return 'RCS MESSAGE';
  }, [rcsConversation, rcsSecondsLeft]);

  // use separate unread tracking instead of conversation UI state
  const rcsUnreadMessages = useSelector(
    state => state.rcs?.unreadMessages || {},
  );
  const unreadRcsMessage = useMemo(() => {
    if (
      rcsConversation?.data?.conversation_status ===
        RCS_CONVERSATION_STATUS_TIMED_OUT ||
      rcsConversation?.data?.conversation_status ===
        RCS_CONVERSATION_STATUS_ENDED
    ) {
      return false; // no indicators once the conversation has ended or timed out
    }

    const separateTracking = rcsUnreadMessages[callerID];
    if (separateTracking !== undefined) {
      return separateTracking;
    }

    // fallback for edge cases
    if (!rcsConversation || !rcsConversation.ui) return false;
    const { unseenMessage } = rcsConversation.ui;
    return unseenMessage;
  }, [rcsConversation, rcsUnreadMessages, callerID]);

  const unreadTF911Message = useMemo(() => {
    if (
      !fullSelectedNumberData?.textFrom911Data ||
      !fullSelectedNumberData?.textFrom911Data.ui
    )
      return false;
    const { hasUnreadMessages } = fullSelectedNumberData.textFrom911Data.ui;
    return hasUnreadMessages || false;
  }, [fullSelectedNumberData]);

  const callStartTime = useMemo(() => {
    if (mostRecentLocation?.call_start_time) {
      return mostRecentLocation.call_start_time;
    } else if (mostRecentLocation?.conversation_start_time) {
      return mostRecentLocation.conversation_start_time;
    } else if (fullSelectedNumberData?.textFrom911Data?.data?.call_start_time) {
      return fullSelectedNumberData.textFrom911Data.data.call_start_time;
    } else if (fullSelectedNumberData?.textFrom911Data?.data?.createdAt) {
      return fullSelectedNumberData.textFrom911Data.data.createdAt;
    }
  }, [fullSelectedNumberData, mostRecentLocation]);

  const checkCallExpired = useCallback(() => {
    const currentTime = new Date().getTime();
    return (currentTime - callStartTime) / 1000 > expiredCallAge;
  }, [callStartTime]);

  const timeExpired = useCallback(() => {
    const currentTime = new Date().getTime();
    const seconds = (currentTime - callStartTime) / 1000;
    let d = Math.floor(seconds / (3600 * 24));
    let h = Math.floor((seconds % (3600 * 24)) / 3600);

    let dDisplay = d > 0 ? d + (d == 1 ? ' day' : ' days') : '';
    let hDisplay = h > 0 ? h + (h == 1 ? ' hour' : ' hours') : '';
    return dDisplay || hDisplay ? `${dDisplay} ${hDisplay} old` : '';
  }, [callStartTime]);

  const handleRemoveCall = useCallback(
    e => {
      e.stopPropagation();
      onRemoveCall({
        caller_id: rightClickedCallerID,
      });
      setDisplayRemoveCall(false);
    },
    [onRemoveCall, rightClickedCallerID],
  );

  // If call is expired, but call start time changed, set it to not expired
  useEffect(() => {
    if (isExpired && !checkCallExpired()) {
      dispatch(setCallExpired({ callerID, isCallExpired: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, callStartTime]);

  useEffect(() => {
    const callExpiredTimer =
      new Date(callStartTime + expiredCallAge * 1000) - new Date();
    // If on mount, the callExpiredTimer is < 0, the callback will be invoked
    const timer = setTimeout(() => {
      if (!isExpired && checkCallExpired()) {
        dispatch(setCallExpired({ callerID, isCallExpired: true }));
      }
    }, callExpiredTimer);

    return () => clearTimeout(timer);
  }, [dispatch, callerID, callStartTime, checkCallExpired, isExpired]);

  const handleViewRcsEventFromToast = useCallback(
    (...params) => {
      onViewRcsEvent(...params);
      trackViewRCSToast({
        conversationId: rcsConversationId,
        sessionId: rcsSessionId,
      });
    },
    [onViewRcsEvent, rcsConversationId, rcsSessionId],
  );

  const handleCloseRcsToast = useCallback(() => {
    trackCloseRCSToast({
      conversationId: rcsConversationId,
      sessionId: rcsSessionId,
    });
    dispatch(dismissRcsToast({ callerID }));
  }, [dispatch, callerID, rcsConversationId, rcsSessionId]);

  useEffect(() => {
    if (!rcsConversation || !rcsConversation.data) return;
    const { conversation_status, serverTimeMillis = new Date().getTime() } =
      rcsConversation.data;

    if (conversation_status !== RCS_CONVERSATION_STATUS_PENDING) {
      return;
    }

    // Only set initial timer state if not already running
    if (!rcsTimerRunning) {
      setRcsTimerRunning(true);
      const initialOffset = serverTimeMillis - new Date().getTime();
      const localTime = new Date().getTime();
      const effectiveTime = new Date(localTime + initialOffset);
      const secondsSinceCallStart = Math.max(
        0,
        Math.floor((effectiveTime - callStartTime) / 1000),
      );
      const initialSecondsLeft = RCS_TIMEOUT - secondsSinceCallStart;
      setRcsSecondsLeft(Math.max(0, initialSecondsLeft));
    }

    const interval = setInterval(() => {
      setRcsSecondsLeft(prevSeconds => {
        const newSeconds = Math.max(0, prevSeconds - 1);

        if (newSeconds >= 0) {
          if (!rcsConversation.ui || !rcsConversation.ui.isRcsToastDismissed) {
            if (isSelected) {
              dispatch(dismissRcsToast({ callerID }));
            } else {
              toastRcsAvailable(
                callerID,
                handleViewRcsEventFromToast,
                handleCloseRcsToast,
              );
            }
          }
        }

        if (newSeconds <= 0) {
          if (
            conversation_status === RCS_CONVERSATION_STATUS_PENDING ||
            conversation_status === RCS_CONVERSATION_STATUS_TIMED_OUT
          ) {
            dispatch(
              setRcsConversationStatus({
                callerID,
                conversation_status: RCS_CONVERSATION_STATUS_TIMED_OUT,
              }),
            );
            triggerTimeoutRcsConversation({
              callerID,
              orgName,
            });
            trackRcsTimeout({
              conversationId: rcsConversationId,
              sessionId: rcsSessionId,
            });
          }
          setRcsTimerRunning(false);
          dispatch(dismissRcsToast({ callerID }));
          clearInterval(interval);
        }

        return newSeconds;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [
    callerID,
    dispatch,
    callStartTime,
    onViewRcsEvent,
    isSelected,
    rcsConversation,
    triggerTimeoutRcsConversation,
    orgName,
    handleCloseRcsToast,
    handleViewRcsEventFromToast,
    rcsConversationId,
    rcsSessionId,
    rcsTimerRunning,
  ]);

  const isRcsAvailable = useMemo(() => {
    return (
      hasGoogleRCSAccess &&
      rcsConversation &&
      rcsConversation.data &&
      rcsConversation.data.conversation_status ===
        RCS_CONVERSATION_STATUS_PENDING
    );
  }, [rcsConversation, hasGoogleRCSAccess]);

  const hasNewMessage =
    unreadTF911Message || isConversationUnread || isUnread || isUnseen;

  const getUnreadStatus = useMemo(() => {
    return (
      isUnread ||
      unreadRcsMessage ||
      unreadTF911Message ||
      isConversationUnread ||
      isCollaboratorUnread ||
      ui.requestedLocationReceived
    );
  }, [
    isUnread,
    unreadRcsMessage,
    unreadTF911Message,
    isConversationUnread,
    isCollaboratorUnread,
    ui,
  ]);

  // Report this item's unread state + element to the queue notifications
  // context so the out-of-view "New messages below" indicator can track it.
  const queueItemRef = useQueueItemNotification(callerID, getUnreadStatus);

  const hasAirAmbulanceQueueStatus = Boolean(
    airAmbulanceStatus || callWithAirAmbulanceRequest,
  );

  useGetRcsConversationMetadataQuery(
    {
      orgName,
      callerID,
    },
    {
      skip:
        !hasGoogleRCSAccess || elsSource !== SOURCE_RCS || !!rcsConversationId,
    },
  );

  useGetConversationMetaData(callerID, orgName, isSMS);

  const isTT911Enabled = useIsTT911Enabled();
  const conversationSource = useSelector(
    state => state.conversations?.[callerID]?.source,
  );
  // Sentry signal when a TCAPI queue entry mounts without the capability —
  // the accept/refresh flow won't work for this PSAP.
  useEffect(() => {
    if (conversationSource === SOURCE_TCAPI && !isTT911Enabled) {
      logMissingTT911Capability({
        callSite: 'EmergencyCall.queueEntry',
        callerId: callerID,
        orgName,
        source: SOURCE_TCAPI,
      });
    }
  }, [callerID, conversationSource, isTT911Enabled, orgName]);

  useEffect(() => {
    if (isSMS && !mostRecentLocation) {
      dispatch(
        fetchLocationShareLocation({
          callerID,
          orgName,
          token,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container
      ref={queueItemRef}
      isExpired={isExpired}
      isHovered={isMapMarkerHovered || isSearchResultHovered}
      isSearchResult={isSearchResult}
      isSelected={isSelected}
      isUnread={getUnreadStatus}
      isUnseen={isUnseen}
      isRcsAvailable={isRcsAvailable}
      unreadTF911Message={unreadTF911Message}
      unreadRcsMessage={unreadRcsMessage}
      onClick={() => {
        setCallerID(callerID);
        dispatch(setHoveredID(null));
      }}
      isCollaborator={isCollaboratorStripeVisible}
    >
      <IconBox>
        <EmergencyCallIcons
          callerID={callerID}
          isManualSearch={isManualSearch}
          isCallReceived={isCallReceived}
          isCallShared={isCallShared}
          isSMS={isSMS}
          isSMSReceived={isSMS && isCallReceived}
          isSMSShared={isSMS && isCallShared}
          isUnseen={isUnseen}
          applicationType={applicationType}
          classOfService={classOfService}
          isPremiumUser={isPremiumUser}
          isAdmin={isAdmin}
          isFord={isFord}
          collaboratorType={collaboratorType}
          pairedAccountName={collaborator?.pairedAccountName}
          isRtiDownstreamSharer={isRtiSharer}
          downstreamAgencies={rawCollaborator?.downstreamAgencies}
        />
      </IconBox>
      <CallBox>
        <PhoneNumber
          isUnseen={isUnseen}
          unreadTF911Message={unreadTF911Message}
          unreadRcsMessage={unreadRcsMessage}
          isUnread={getUnreadStatus}
        >
          {primaryLabel || formatInternationalPhoneNumberHuman(callerID)}
        </PhoneNumber>
        {ungeocodedCallsEnabled && isUnplottableAddress(mostRecentLocation) && (
          <UnplottableAddressNotice
            address={getCivicAddress(mostRecentLocation)}
            variant="queue"
          />
        )}
        {eventShareData.length > 0 && (
          <RegularNewMessage>Shared Event</RegularNewMessage>
        )}
        {/* RTI-routed calls show this via the icon's tooltip instead. */}
        {isCollaboratorStripeVisible && (
          <CollaboratorMessage>
            {getCollaboratorTypeDisplayName(
              collaboratorType,
              collaborator?.pairedAccountName,
            )}
          </CollaboratorMessage>
        )}
        <TextMessageStatus
          isTT911={isTT911}
          source={source}
          classOfServiceCategory={classOfService}
          hasNewMessage={hasNewMessage}
          unreadRcsMessage={unreadRcsMessage}
          rcsConversationStatus={rcsConversation?.data?.conversation_status}
          requestedLocationReceived={ui.requestedLocationReceived}
          tt911Message={tt911Message}
          rcsStatusText={rcsStatusText}
          hasAgent511Access={hasAgent511Access}
          primaryChatKey={primaryChatKey}
          isOutboundSMS={
            fullSelectedNumberData?.textFrom911Data?.data?.isOutboundSMS
          }
        />
        <LiveVideoSession callerId={callerID} />
        <MediaAvailable callerId={callerID} />
        <ZeroEyesEvent callerId={callerID} />

        {isAdmin && <PhoneDetails>Admin Line Call</PhoneDetails>}

        {callStatusDisplayName && !hasAirAmbulanceQueueStatus && (
          <CallStatusTag status={callStatus} isCallPopup={false}>
            <CallStatus data-name="call-status" status={callStatus}>
              {callStatusDisplayName}
            </CallStatus>
          </CallStatusTag>
        )}
        {hasAirAmbulanceQueueStatus && (
          <AirAmbulanceStatus data-name="air-ambulance-status">
            {callStatusDisplayName}
          </AirAmbulanceStatus>
        )}

        {isConnectedCar && (
          <SiriusXMMessage>Veh. Crash Detected</SiriusXMMessage>
        )}
        {isApple && <PhoneDetails>Add'l Data Avail.</PhoneDetails>}
        {googleIndicator && <PhoneDetails>{googleIndicator}</PhoneDetails>}
        {isCallerIn911informBuilding && (
          <PhoneDetails>Connected Building</PhoneDetails>
        )}
        {isCallerInConnectedSitesBuilding && (
          <ConnectedSiteMessage>Connected Site</ConnectedSiteMessage>
        )}

        {isExpired && (
          <>
            <PhoneDetails>{timeExpired()}</PhoneDetails>
            <PhoneDetails>Right click to remove</PhoneDetails>
          </>
        )}

        <OnsitePersonnelNotifiedText callerId={callerID} />
      </CallBox>
      {callStartTime ? (
        <TimeBox>
          <Time>{displayMilitaryTime(callStartTime)}</Time>
        </TimeBox>
      ) : null}

      <animated.div style={animateProps}>
        <RemoveCall onClick={handleRemoveCall}>Remove</RemoveCall>
      </animated.div>
    </Container>
  );
};

export default EmergencyCall;
